import { usePricingRights } from '@util/PricingRightsHook';
import React, { useEffect, useRef } from 'react';
import CalendarDay from '@app/state/calendar/CalendarDay';
import Utilities from '@util/Utilities';
import { ArrowDownIcon, ArrowUpIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import Spinner from '@app/component/static/Spinner';
import Tag from '@app/component/tag/Tag';
import { Tooltip } from 'react-tooltip';
import { MarketStateStatDateCompetitorSnapshot } from '@app/model/market_state/MarketStateStatDateCompetitorSnapshot';
import CalendarSettingsDefaultDisplayType from '@app/model/company_hotel/settings/CalendarSettingsDefaultDisplayType';
import AvailabilityDailyStateProvider
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/AvailabillityDailyStateProvider';
import AvailabilityStateDailySnapshotTooltip
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/AvailabilityStateDailySnapshotTooltip';
import {
	renderMarketAreaStateIcon,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/component/DisplayMarketAreaStateTag';
import AlertIndicator
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/fragment/AlertIndicator';
import EmptyCalendarDayFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_desktop/fragment/empty/EmptyCalendarDayFragment';
import {
	useMarketAreaStateForStatDate,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketAreaStateForMonth';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import MarketStateDailySnapshotState
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateDailySnapshotState';
import {
	useFindMarketStateNotificationsForStatDate,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useFindMarketStateNotificationsForStatDate';
import MarketRegionStateCluster from '@app/model/market_area/MarketRegionStateCluster';
import CompanyHotelSettingsCalendarDayPopupTriggerType
	from '@app/model/company_hotel/settings/CompanyHotelSettingsCalendarDayPopupTriggerType';
import CompanyHotelSettingsPriceMedianIndicationType
	from '@app/model/company_hotel/settings/CompanyHotelSettingsPriceMedianIndicationType';
import { toColor } from '@app/component/tag/TagState';
import _isEmpty from 'lodash/isEmpty';
import Price from '@app/model/market_state/Price';
import PriceRecommendationStateSnapshot from '@app/model/market_state/output/PriceRecommendationStateSnapshot';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import CalendarSettingsPriceRecommendationIndicatorType
	from '@app/model/company_hotel/settings/CalendarSettingsPriceRecommendationIndicatorType';
import {
	useLatestProcessRequestExecutionStateMonitor,
} from '@app/site/secured/rateshopper/rates/provider/useLatestProcessRequestExecutionStateMonitor';
import Process from '@app/model/process-request/Process';
import type { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import { findOtherRoomTypeRecommendation, isSubmittedRecommendation } from './MarketStateSnapshotCalendarDay.utilities';


export const MarketStateSnapshotCalendarDay: React.FunctionComponent<{
	index: number,
	selectedCalendarDay: CalendarDay | undefined,
	setSelectedCalendarDay: (day: CalendarDay | undefined) => void,
	marketStateDailySnapshotState: MarketStateDailySnapshotState,
}>
	= ({
			index,
			marketStateDailySnapshotState,
			selectedCalendarDay,
			setSelectedCalendarDay,
		}) => {
	const div = useRef<HTMLDivElement>(null);
	const _marketStateDailySnapshotState = marketStateDailySnapshotState;

	const tools = useMarketStateTools();
	const marketStateNotificationsForDay = useFindMarketStateNotificationsForStatDate(_marketStateDailySnapshotState.calendarDay.display);

	const tooltipId = `tooltip_calendar_${_marketStateDailySnapshotState.calendarDay.display}`;

	const monitor = useLatestProcessRequestExecutionStateMonitor();
	const showUpdateSuccess = _marketStateDailySnapshotState.calendarDay.isCurrentMonth && !_marketStateDailySnapshotState.calendarDay.isPast &&
		isMarketStateCalendarProcess(monitor.latestCompletedExecutionState);
	const isUpdating = _marketStateDailySnapshotState.calendarDay.isCurrentMonth && !_marketStateDailySnapshotState.calendarDay.isPast &&
		monitor.isInteractionLocked &&
		isMarketStateCalendarProcess(monitor.latestActiveExecutionState);

	useEffect(() => {
		if (div.current !== null) {
			if (selectedCalendarDay?.display === _marketStateDailySnapshotState.calendarDay.display) {
				div.current.focus();
			}
		}
	}, [selectedCalendarDay]);

	const onSelect = () => {
		if (monitor.isInteractionLocked) return;
		if (selectedCalendarDay?.display === _marketStateDailySnapshotState.calendarDay.display) return;
		setSelectedCalendarDay(_marketStateDailySnapshotState.calendarDay);
	};

	const marketAreaStateComparisonOutput = useMarketAreaStateForStatDate(_marketStateDailySnapshotState.calendarDay.display);
	const currentMarketAreaState = marketAreaStateComparisonOutput?.current;

	const showMarketAreaState = !!currentMarketAreaState && (currentMarketAreaState.cluster === MarketRegionStateCluster.VERY_HIGH || currentMarketAreaState.cluster === MarketRegionStateCluster.VERY_LOW);

	if (!_marketStateDailySnapshotState.marketStateDailySnapshot || _marketStateDailySnapshotState.marketStateDailySnapshot.needsRefreshResults())
		return <EmptyCalendarDayFragment index={index} marketStateDailySnapshotState={_marketStateDailySnapshotState}
		selectedCalendarDay={selectedCalendarDay} />;

	const marketStateDailyMainHotelSnapshot = _marketStateDailySnapshotState.marketStateDailySnapshot.current.findDailyMainHotelSnapshot();
	const recommendationSnapshot = _marketStateDailySnapshotState.marketStateDailySnapshot.priceRecommendationStateSnapshot;

	if (!marketStateDailyMainHotelSnapshot) {
		return <EmptyCalendarDayFragment index={index} marketStateDailySnapshotState={_marketStateDailySnapshotState}
		selectedCalendarDay={selectedCalendarDay} />;
	}

	const evaluatePriceMedian = _marketStateDailySnapshotState.marketStateDailySnapshot!.current.evaluatePriceMedian(tools.companyHotel);

	let extraBackgroundClass = '';
	if (tools.companyHotel.settings.calendar.priceMedianIndicationType === CompanyHotelSettingsPriceMedianIndicationType.ICON_AND_BACKGROUND && !_isEmpty(evaluatePriceMedian)) {
		extraBackgroundClass = `bg-gradient-to-b from-${toColor(evaluatePriceMedian[0]!.state)}-50`;
	}

	return <div tabIndex={index + 1}
	id={`calendar-day-desktop-${_marketStateDailySnapshotState.calendarDay.display}`}
	data-tooltip-id={tooltipId}
	ref={div}
	onMouseEnter={tools.companyHotel.settings.calendar.calendarDayPopupTriggerType === CompanyHotelSettingsCalendarDayPopupTriggerType.ON_HOVER ? onSelect : undefined}
	onClick={tools.companyHotel.settings.calendar.calendarDayPopupTriggerType === CompanyHotelSettingsCalendarDayPopupTriggerType.ON_CLICK ? onSelect : undefined}
	className={Utilities.classNames('min-h-[132px] hover:bg-gray-50 cursor-pointer hover:shadow-lg focus:outline-none focus:border-sky-300 focus:ring-1 focus:ring-sky-300',
								(_marketStateDailySnapshotState.calendarDay.isPreviousMonth || _marketStateDailySnapshotState.calendarDay.isNextMonth) ? 'relative bg-gray-50 text-gray-500' : 'relative bg-white')}>
		<div className={Utilities.classNames('flex flex-col justify-between h-full', extraBackgroundClass)}>
			<div className="flex justify-between px-3 py-2">
				<time dateTime={_marketStateDailySnapshotState.calendarDay.display}
				className={Utilities.classNames(_marketStateDailySnapshotState.calendarDay.isToday ? 'flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 font-semibold text-white' : '')}>{_marketStateDailySnapshotState.calendarDay.index.day}</time>
				<div className="flex relative top-1">
					<div
						className={Utilities.classNames('absolute right-0 top-0 text-green-300 opacity-0 transition-opacity ease-in-out delay-150 duration-500', showUpdateSuccess ? 'opacity-100' : '')}>
						<CheckCircleIcon className="h-5 w-5" />
					</div>
					{isUpdating && <Spinner width={3} height={3} />}
				</div>
			</div>
			<div
				className={Utilities.classNames('flex flex-col items-center justify-center ', _marketStateDailySnapshotState.calendarDay.isPast ? 'font-base italic text-gray-300' : 'font-bold')}>
				<DisplayCalendarIndicator marketStatDateMainHotelSnapshot={marketStateDailyMainHotelSnapshot}
				recommendationSnapshot={recommendationSnapshot}
				dailySnapshotState={_marketStateDailySnapshotState}
				calendarDay={_marketStateDailySnapshotState.calendarDay} />
			</div>

			<div className="flex border-t border-gray-200/70 divide-x divide-gray-100">
				<div className="flex grow items-center px-2 h-9">
					{evaluatePriceMedian!.map((result, idx) =>
						<Tag key={`index_${idx}`} state={result.state}>{result.value}</Tag>)}
					{tools.companyHotel.settings.calendar.defaultDisplayType === CalendarSettingsDefaultDisplayType.AVAILABILITY
						&&
						<AvailabilityDailyStateProvider companyHotel={tools.companyHotel}
						statDate={_marketStateDailySnapshotState.marketStateDailySnapshot.statDate}>
							<AvailabilityStateDailySnapshotTooltip openOnClick={false}
							id={`availability-tooltip-${_marketStateDailySnapshotState.marketStateDailySnapshot.statDate}`} />
						</AvailabilityDailyStateProvider>}

					{_marketStateDailySnapshotState.marketStateDailySnapshot!.current.renderIndicator(tools.companyHotel)!.map((result, idx) => {
						return <div key={`index_${idx}`}
						data-tooltip-id={`${_marketStateDailySnapshotState.marketStateDailySnapshot!.current.latestUpdateDate}_tooltip`}
						data-tooltip-content={result.description}>
							<Tag state={result.state} extraClassName="ml-2">{result.value}</Tag>
							<Tooltip
								id={`${_marketStateDailySnapshotState.marketStateDailySnapshot!.current.latestUpdateDate}_tooltip`} />
						</div>;
					})}
				</div>
				{showMarketAreaState && <div className="flex items-center px-2 h-9">
					{renderMarketAreaStateIcon(currentMarketAreaState)}
				</div>}
				<div className="flex items-center px-2 h-9">
					<AlertIndicator marketStatDateCompetitorSnapshot={marketStateDailyMainHotelSnapshot}
					calendarDay={_marketStateDailySnapshotState.calendarDay}
					marketStateNotificationsForDay={marketStateNotificationsForDay} />
				</div>
			</div>
		</div>
	</div>;
};

const isMarketStateCalendarProcess = (processRequestExecutionState: { process: Process } | null | undefined) => {
	return processRequestExecutionState?.process === Process.COMPANY_HOTEL_MARKET_STATE ||
		processRequestExecutionState?.process === Process.COMPANY_HOTEL_MARKET_STATE_OTA ||
		processRequestExecutionState?.process === Process.PRICE_RECOMMENDATION;
};

const DisplayCalendarIndicator: React.FunctionComponent<{
	marketStatDateMainHotelSnapshot: MarketStateStatDateCompetitorSnapshot | undefined,
	recommendationSnapshot: PriceRecommendationStateSnapshot | undefined,
	dailySnapshotState: MarketStateDailySnapshotState,
	calendarDay: CalendarDay
}> = ({ marketStatDateMainHotelSnapshot, recommendationSnapshot, dailySnapshotState, calendarDay }) => {

	const tools = useMarketStateTools();

	const { canViewRecommendations } = usePricingRights(tools.companyHotel);
	const showRecommendationTag = canViewRecommendations && !calendarDay.isPast && !!recommendationSnapshot && tools.companyHotel.hasRecommendationNormalEngineRoompulseOrBraintree() && !recommendationSnapshot?.isSubmitted;
	const showPriceRecommendationLiteTag = canViewRecommendations && !calendarDay.isPast && tools.companyHotel.hasRecommendationEngineRoompulseLite();

	const otherRoomTypeRecommendation = findOtherRoomTypeRecommendation(dailySnapshotState, tools.companyHotel, canViewRecommendations);
	const isOtherRoomTypeSubmitted = isSubmittedRecommendation(dailySnapshotState, otherRoomTypeRecommendation);
	const isSubmitted = recommendationSnapshot?.isSubmitted === true || isOtherRoomTypeSubmitted;

	if (marketStatDateMainHotelSnapshot === undefined && calendarDay.isPast && calendarDay.isCurrentMonth)
		return <>
			<span className="text-lg text-center block mt-2 mb-5 text-gray-300">--</span>
			<span />
		</>;
	else if (marketStatDateMainHotelSnapshot !== undefined) {
		if (marketStatDateMainHotelSnapshot.isAvailable()) {
			return <>
			<span className={Utilities.classNames('text-lg text-center block')}>
				{Utilities.formatPrice(marketStatDateMainHotelSnapshot.getLowestItemForRoom().lowestItem.item.price)}
				{isSubmitted && <span className="ml-1 inline-flex align-middle text-blue-600" title="Submitted"><span className="sr-only">Submitted</span><CheckCircleIcon className="h-4 w-4" aria-hidden="true" /></span>}
			</span>
				{showRecommendationTag &&
					<DisplayPriceRecommendationTag recommendationSnapshot={recommendationSnapshot}
					currentPrice={marketStatDateMainHotelSnapshot.getLowestItemForRoom().lowestItem.item.price} />}
				{!showRecommendationTag && otherRoomTypeRecommendation !== undefined && !isOtherRoomTypeSubmitted &&
					<DisplayOtherRoomTypeRecommendationTag recommendation={otherRoomTypeRecommendation} />}
				{showPriceRecommendationLiteTag &&
					<DisplayPriceRecommendationLiteTag calendarDay={calendarDay} />}
			</>;
		} else if (marketStatDateMainHotelSnapshot.isSoldOut()) {
			if (marketStatDateMainHotelSnapshot.isLos()) {
				return <>
					<span className={Utilities.classNames('text-base font-light text-md text-gray-400 text-center')}>
							LOS {marketStatDateMainHotelSnapshot.getLowestItemForRoom().lowestItem.item.leastPeriodOfStay}
					</span>
					<span className="block font-light text-lg text-gray-300 text-center">
						({Utilities.formatPrice(marketStatDateMainHotelSnapshot.getLowestItemForRoom().lowestItem.item.price)})
						{isSubmitted && <span className="ml-1 inline-flex align-middle text-blue-600" title="Submitted"><span className="sr-only">Submitted</span><CheckCircleIcon className="h-4 w-4" aria-hidden="true" /></span>}
					</span>
				</>;
			} else {
				return <span
					className="text-base font-light text-md text-gray-400 text-center">
					Sold Out
					{isSubmitted && <span className="ml-1 inline-flex align-middle text-blue-600" title="Submitted"><span className="sr-only">Submitted</span><CheckCircleIcon className="h-4 w-4" aria-hidden="true" /></span>}
				</span>;
			}
		}
	}
	return <span
		className="text-base font-light text-md text-gray-400 text-center">Not Available</span>;
};

const DisplayPriceRecommendationTag: React.FunctionComponent<{
	recommendationSnapshot: PriceRecommendationStateSnapshot,
	currentPrice: Price
}> = ({ recommendationSnapshot, currentPrice }) => {

	const {companyHotel} = useSelectedCompanyHotel();

	if (!recommendationSnapshot.hasValidRecommendation()) return null;

	const shouldIncrease = Number(recommendationSnapshot.recommendedPrice) > Number(currentPrice.amount);

	return <Tag state="none" extraClassName={`mt-0.5 mb-4 ${shouldIncrease ? '!text-green-500' : '!text-rose-500'}`}>
		{shouldIncrease ? <ArrowUpIcon className="w-3 h-3 -ml-1" /> : <ArrowDownIcon className="w-3 h-3 -ml-1" />}
		{companyHotel.settings.calendar.priceRecommendationIndicator === CalendarSettingsPriceRecommendationIndicatorType.AMOUNT && Utilities.formatAmount(recommendationSnapshot.recommendedPrice, currentPrice.currency, 0)}
		{companyHotel.settings.calendar.priceRecommendationIndicator === CalendarSettingsPriceRecommendationIndicatorType.PERCENTAGE && Utilities.formatDouble(recommendationSnapshot.effectiveChangeRate * 100,  0) + '%'}
	</Tag>;
};

// The cell price belongs to another room type, so this tag is percent-only in both indicator modes.
const DisplayOtherRoomTypeRecommendationTag: React.FunctionComponent<{
	recommendation: PriceRecommendationState,
}> = ({ recommendation }) => {
	const shouldIncrease = recommendation.effectiveChangeRate > 0;
	return <Tag state="none" extraClassName={`mt-0.5 mb-4 ${shouldIncrease ? '!text-green-500' : '!text-rose-500'}`}>
		{shouldIncrease ? <ArrowUpIcon className="w-3 h-3 -ml-1" /> : <ArrowDownIcon className="w-3 h-3 -ml-1" />}
		{Utilities.formatDouble(recommendation.effectiveChangeRate * 100, 0) + '%*'}
	</Tag>;
};

const DisplayPriceRecommendationLiteTag: React.FunctionComponent<{
	calendarDay: CalendarDay,
}> = ({ calendarDay }) => {

	const tools = useMarketStateTools();

	const hasRecommendation = tools.marketStateSnapshot.findPriceRecommendationState(calendarDay.display);

	return hasRecommendation ? <div
		className="inline-flex items-center rounded-md bg-cyan-50 px-1.5 py-0.5 text-xs font-medium text-cyan-700 ring-1 ring-inset ring-cyan-700/10 mt-0.5 mb-4">
		Review Req.
	</div> : null;
};
